/**
 * The "citation view" for keeping web pages (spec §3.3, K2a): each web page
 * the stored answer used, with a checkbox (none ticked by default), its
 * title, domain, an "open" link and the snippet, so the user can check a page
 * before keeping it. Pages already kept are marked and cannot be ticked.
 * Presentational: the controller (KeepWebAnswer) owns the I/O.
 */

import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Link,
  Typography,
} from "@mui/material";
import { ExternalLink } from "lucide-react";
import type { Citation } from "../../../chat/types";
import { domainOf, pagesLabel } from "./webAnswer";

export interface KeepWebPagesDialogProps {
  open: boolean;
  /** The stored answer's web pages (http(s) only — see webSourcesOf). */
  pages: Citation[];
  /** Stored citation URLs the caller already keeps. */
  keptUrls: ReadonlySet<string>;
  /** Still looking up the stored row of a just-streamed answer. */
  preparing?: boolean;
  saving: boolean;
  error: string | null;
  onKeep: (urls: string[]) => void;
  onClose: () => void;
}

export function KeepWebPagesDialog({
  open,
  pages,
  keptUrls,
  preparing = false,
  saving,
  error,
  onKeep,
  onClose,
}: KeepWebPagesDialogProps) {
  const [picked, setPicked] = useState<ReadonlySet<string>>(() => new Set());
  // Start from nothing ticked whenever the dialog opens.
  useEffect(() => {
    if (open) setPicked(new Set());
  }, [open]);

  const toggle = (url: string) =>
    setPicked((prev) => {
      const next = new Set(prev);
      if (next.has(url)) next.delete(url);
      else next.add(url);
      return next;
    });

  // Request order follows the list, and a page that turned out to be kept
  // already is never sent.
  const chosen = pages.map((p) => p.url!).filter((u) => picked.has(u) && !keptUrls.has(u));

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} fullWidth maxWidth="sm">
      <DialogTitle>Keep web pages</DialogTitle>
      <DialogContent dividers>
        {preparing && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
            <CircularProgress size={14} />
            <Typography variant="body2" color="text.secondary">
              Still saving the answer…
            </Typography>
          </Box>
        )}
        <Box component="ul" sx={{ listStyle: "none", p: 0, m: 0, mb: 2 }}>
          {pages.map((page) => {
            const url = page.url!;
            const title = page.title || url;
            const kept = keptUrls.has(url);
            return (
              <Box
                component="li"
                key={url}
                sx={{ display: "flex", gap: 1, alignItems: "flex-start", mb: 1.5 }}
              >
                <Checkbox
                  size="small"
                  checked={kept || picked.has(url)}
                  disabled={kept || saving}
                  onChange={() => toggle(url)}
                  slotProps={{ input: { "aria-label": `Keep ${title}` } }}
                  sx={{ p: 0.5, mt: -0.25 }}
                />
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Box sx={{ display: "flex", gap: 1, alignItems: "baseline", flexWrap: "wrap" }}>
                    <Typography variant="body2" sx={{ fontWeight: 500, wordBreak: "break-word" }}>
                      {title}
                    </Typography>
                    <Link
                      href={url}
                      target="_blank"
                      rel="noopener noreferrer"
                      variant="caption"
                      aria-label={`Open ${title}`}
                      sx={{ display: "inline-flex", alignItems: "center", gap: 0.25 }}
                    >
                      <span>{domainOf(url)}</span>
                      <ExternalLink size={12} aria-hidden />
                    </Link>
                  </Box>
                  {kept && (
                    <Typography variant="caption" color="text.secondary" component="p">
                      Already in your knowledge base
                    </Typography>
                  )}
                  {page.snippet && (
                    <Typography variant="caption" color="text.secondary" component="p">
                      {page.snippet}
                    </Typography>
                  )}
                </Box>
              </Box>
            );
          })}
        </Box>
        <Typography variant="caption" color="text.secondary" component="p">
          Each page is saved as a personal document. You can share it from the Knowledge page.
        </Typography>
        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button color="inherit" onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={() => onKeep(chosen)}
          disabled={chosen.length === 0 || saving}
          startIcon={saving ? <CircularProgress size={14} /> : undefined}
        >
          Keep {pagesLabel(chosen.length)}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
